"use client";

import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  trackAuthLandingView,
  trackAuthSigninPageEnter,
  trackAuthSigninTap,
  trackObCarouselView,
} from "@/src/hooks/onboarding/onboardingAnalytics";
import { setAuthSigninEntry } from "@/src/hooks/onboarding/authSigninEntry";
import { cn } from "utils";
import { OnboardingCarousel } from "../OnboardingCarousel";
import { IntroActiveIndexProvider } from "./IntroActiveIndexContext";
import { IntroSlideShell } from "./IntroSlideShell";
import { IntroPrimaryAuthCta } from "./IntroPrimaryAuthCta";
import { IntroHeadline } from "./IntroHeadline";
import { SIGN_IN_INTRO_CAROUSEL_INTERVAL_MS } from "./introSlidesConfig";
import { resolveIntroAuthPlatform } from "./resolveIntroAuthPlatform";
import { createSignInIntroCopy } from "./signInIntroCopy";
import type { SignInIntroPhaseProps } from "./types";
import { IntroSlide1Hero } from "./slides/IntroSlide1Hero";
import { IntroSlide2Radar } from "./slides/IntroSlide2Radar";
import { IntroSlide3Goals } from "./slides/IntroSlide3Goals";
import { IntroSlide4Activity } from "./slides/IntroSlide4Activity";
import { useParams } from "next/navigation";

export const SignInIntroPhase: FC<SignInIntroPhaseProps> = ({
  onGetStarted,
  onApple,
  onGoogle,
  className,
  initialSlideIndex = 0,
  freezeCarousel = false,
  platform: platformProp,
}) => {
  const { locale = "en" } = useParams() as { locale?: string };
  const [activeIndex, setActiveIndex] = useState(initialSlideIndex);
  const copy = useMemo(() => createSignInIntroCopy(locale), [locale]);
  const platform = useMemo(
    () => platformProp ?? resolveIntroAuthPlatform(),
    [platformProp],
  );

  useEffect(() => {
    trackObCarouselView();
    trackAuthLandingView(platform);
  }, [platform]);

  const handleChange = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const handleGetStarted = useCallback(() => {
    setAuthSigninEntry("signin_page");
    trackAuthSigninPageEnter({
      platform,
      slide_index: activeIndex,
      button: "get_started",
    });
    onGetStarted();
  }, [platform, activeIndex, onGetStarted]);

  const handleOtherOptions = useCallback(() => {
    setAuthSigninEntry("signin_page");
    trackAuthSigninPageEnter({
      platform,
      slide_index: activeIndex,
      button: "other_options",
    });
    onGetStarted();
  }, [platform, activeIndex, onGetStarted]);

  const handleApple = useCallback(() => {
    setAuthSigninEntry("carousel_primary");
    trackAuthSigninTap({
      platform,
      entry: "carousel_primary",
      slide_index: activeIndex,
      method: "apple",
    });
    onApple?.();
  }, [platform, activeIndex, onApple]);

  const handleGoogle = useCallback(() => {
    setAuthSigninEntry("carousel_primary");
    trackAuthSigninTap({
      platform,
      entry: "carousel_primary",
      slide_index: activeIndex,
      method: "google",
    });
    onGoogle?.();
  }, [platform, activeIndex, onGoogle]);

  const items = useMemo(() => {
    return [
      {
        id: "intro-1",
        content: (
          <IntroSlideShell
            slideIndex={0}
            headline={
              <IntroHeadline
                prefix={copy.intro1.headlinePrefix}
                accent={copy.intro1.headlineAccent}
                suffix={copy.intro1.headlineSuffix}
              />
            }
            subtitle={copy.intro1.subtitle}
          >
            <IntroSlide1Hero />
          </IntroSlideShell>
        ),
      },
      {
        id: "intro-2",
        content: (
          <IntroSlideShell
            slideIndex={1}
            hideDesktopHero
            desktopVisual={<IntroSlide2Radar layout="desktop" />}
            headline={
              <IntroHeadline
                prefix={copy.intro2.headlinePrefix}
                accent={copy.intro2.headlineAccent}
                suffix={copy.intro2.headlineSuffix}
              />
            }
            subtitle={copy.intro2.subtitle}
          >
            <IntroSlide2Radar layout="mobile" />
          </IntroSlideShell>
        ),
      },
      {
        id: "intro-3",
        content: (
          <IntroSlideShell
            slideIndex={2}
            hideDesktopHero
            desktopVisual={<IntroSlide3Goals layout="desktop" />}
            headline={<IntroHeadline text={copy.intro3.headline} />}
            subtitle={copy.intro3.subtitle}
          >
            <IntroSlide3Goals layout="mobile" />
          </IntroSlideShell>
        ),
      },
      {
        id: "intro-4",
        content: (
          <IntroSlideShell
            slideIndex={3}
            hideDesktopHero
            desktopVisual={<IntroSlide4Activity layout="desktop" />}
            headline={<IntroHeadline text={copy.intro4.headline} />}
            subtitle={copy.intro4.subtitle}
          >
            <IntroSlide4Activity layout="mobile" />
          </IntroSlideShell>
        ),
      },
    ];
  }, [copy]);

  // 与 Figma 5s dwell / 进度条同拍；harness 定格时关闭
  const autoplay = !freezeCarousel;

  return (
    <IntroActiveIndexProvider value={activeIndex}>
      <div
        className={cn("relative h-dvh w-full overflow-hidden", className)}
        data-testid="sign-in-intro-phase"
        data-autoplay={autoplay ? "on" : "off"}
        data-freeze-carousel={freezeCarousel ? "true" : "false"}
      >
        <OnboardingCarousel
          items={items}
          autoplay={autoplay}
          interval={SIGN_IN_INTRO_CAROUSEL_INTERVAL_MS}
          initialIndex={initialSlideIndex}
          showIndicator={false}
          className="h-full"
          onChange={handleChange}
        />
        <IntroPrimaryAuthCta
          platform={platform}
          slideIndex={activeIndex}
          onApple={handleApple}
          onGoogle={handleGoogle}
          onGetStarted={handleGetStarted}
          onOtherOptions={handleOtherOptions}
        />
      </div>
    </IntroActiveIndexProvider>
  );
};
